import { useMutation } from '@apollo/client';
import { Avatar } from 'components/Avatar/Avatar';
import { Button } from 'components/Button/Button';
import Checkbox from 'components/Checkbox/Checkbox';
import { Date } from 'components/Date/Date';
import { Modal } from 'components/Modal/Modal';
import { Portal } from 'components/Portal/Portal';
import { Table, TableHeader, TableRow, TableHead, TableCell, TableBody } from 'components/Table/Table';
import { useToast } from 'components/Toast/use-toast';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EMAIL_EXPIRING_MEMBERSHIP } from 'services/users/mutation';

type ExpiringMembershipsModalProps = {
    isOpen: boolean;
    onClose: () => void;
    members: any[];
    onEmailSent?: () => void;
};
export const ExpiringMembershipsModal: React.FC<ExpiringMembershipsModalProps> = ({ isOpen, onClose, members, onEmailSent }) => {
    const [allSelected, setAllSelected] = useState(false);
    const [selectedMembers, setSelectedMembers] = useState<any>([]);
    const { t } = useTranslation('dashboard');
    const { toast } = useToast();

    const [expiredMembershipsMutation, { loading }] = useMutation(EMAIL_EXPIRING_MEMBERSHIP);

    const selectAll = () => {
        if (allSelected) {
            setSelectedMembers([]);
            setAllSelected(false);
            return;
        }
        setAllSelected(true);
        setSelectedMembers(members.map(m => m.id));
    };

    const selectMember = (id: string) => {
        if (allSelected) setAllSelected(false);
        if (selectedMembers.includes(id)) {
            setSelectedMembers(selectedMembers.filter((member: any) => member !== id));
            return;
        }
        setSelectedMembers([...selectedMembers, id]);
    };

    const notifyMembers = async () => {
        if (!selectedMembers.length) return;

        const resp = await expiredMembershipsMutation({
            variables: { members: selectedMembers },
        });

        if (resp?.data?.sendEmailExpiringMembership?.success) {
            toast({
                title: t('expiringMembershipsSuccess'),
                variant: 'success',
            });
            onEmailSent?.(); // Refetch data to update email sent status
            onClose();
            setSelectedMembers([]);
        } else {
            toast({
                title: t('failToastTitle'),
                description: t('failToastSubtitle'),
                variant: 'warning',
            });
        }
    };

    return (
        <Portal>
            <Modal isOpen={isOpen} onClose={onClose} maxWidth={800}>
                <div className="flex flex-col gap-6">
                    <div>
                        <div className="text-xl font-semibold text-zinc-700 mb-2">
                            {t('expiringMembershipsModalTitle')}
                        </div>
                        <div className="text-sm text-zinc-500">{t('expiringMembershipsModalSubtitle')}</div>
                    </div>
                    <div className="flex flex-col gap-4">
                        <Table className="border">
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="flex gap-2 items-center">
                                        <Checkbox checked={allSelected} onChange={() => selectAll()} />
                                        {t('nameLabel')}
                                    </TableHead>
                                    <TableHead />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {members?.length > 0 &&
                                    members.map((member: any) => (
                                        <TableRow
                                            key={member.id}
                                            onClick={() => selectMember(member.id)}
                                            className="cursor-pointer"
                                        >
                                            <TableCell className="flex gap-2 items-center w-full">
                                                <Checkbox
                                                    onChange={() => selectMember(member.id)}
                                                    checked={selectedMembers.includes(member.id)}
                                                />
                                                <Avatar image={member.image} size={32} />
                                                <div className="flex items-center gap-2">
                                                    <span>{member.firstName} {member.lastName}</span>
                                                    {member.hasRecentMembershipEmailSent && (
                                                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                                            {t('emailSent')}
                                                        </span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Date date={member.membershipEnd} />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                        <div className="flex justify-end gap-2">
                            <Button
                                variant="outline"
                                disabled={selectedMembers.length === 0 || loading}
                                onClick={() => notifyMembers()}
                            >
                                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {t('sendEmailButton')}
                            </Button>
                            <Button onClick={onClose}>{t('closeButton')}</Button>
                        </div>
                    </div>
                </div>
            </Modal>
        </Portal>
    );
};
